import { TicketsAuth } from "@mg/schemas/src/auth";
import {
  GetKnowledgeSettingsResponse,
  GetTicketResponse,
} from "@mg/schemas/src/christo/catalyst";
import {
  type CreativeBriefConfig,
  type DeliveryConfig,
  type DesignReviewConfig,
  type Enterprise,
  EnterpriseProfileType,
  type Ticket,
  TicketWorkflowStep,
  UserPermission,
  type WorkflowUserTypes,
  type WorkflowLimitedUserTypes,
  type User,
} from "@mg/schemas/src/commons";
import { redirect } from "@tanstack/react-router";

import { queryClient } from "./queryClient";
import { store } from "./store";

import { flagPayload, flagVariant, isFlagOn } from "../config/featureFlags";
import { loginRoute } from "../routes/login/route";

export function requiresAuth() {
  const { value: user } = store.getState().auth;

  if (user == null) {
    throw redirect({
      to: loginRoute.to,
    });
  }
}

export function isPunttGuest() {
  const { value: user } = store.getState().auth;

  return (
    user != null && user.role == null && !!(user.ticketId || user.folderId)
  );
}

export function isUserCreative() {
  const { value } = store.getState().auth;

  return (
    !value?.permissions?.includes(UserPermission.CONNECT_TEAM) &&
    !value?.permissions?.includes(UserPermission.ENTERPRISE_ADMIN) &&
    value?.type !== "client"
  );
}

export function canAssignCreatives(ticket: Ticket) {
  const { value } = store.getState().auth;

  // always true for role mg and admin
  if (
    value?.role === EnterpriseProfileType.MEANINGFUL_GIGS ||
    value?.role === EnterpriseProfileType.ADMIN ||
    value?.role === EnterpriseProfileType.ADMIN_WITH_AI
  ) {
    return true;
  }

  const sendToDesign = (
    ticket.workflow?.steps?.find(
      (step) => step.type === TicketWorkflowStep.BRIEF,
    )?.config as CreativeBriefConfig
  )?.sendToDesign;

  if (!sendToDesign) {
    return true;
  }

  if (
    sendToDesign.canAssignCreatives.userIds.includes(value?.userID as string)
  ) {
    return true;
  }

  if (
    sendToDesign.canAssignCreatives.roles.includes(
      value?.role as unknown as WorkflowLimitedUserTypes,
    )
  ) {
    return true;
  }

  return false;
}

export function canAssignReviewers(ticket: Ticket) {
  const { value } = store.getState().auth;

  // always true for role mg and admin
  if (
    value?.role === EnterpriseProfileType.MEANINGFUL_GIGS ||
    value?.role === EnterpriseProfileType.ADMIN ||
    value?.role === EnterpriseProfileType.ADMIN_WITH_AI
  ) {
    return true;
  }
  const reviewConfig = (
    ticket.workflow?.steps?.find(
      (step) => step.type === TicketWorkflowStep.DESIGN_REVIEW,
    )?.config as DesignReviewConfig
  )?.requiresApproval?.canAssignReviewers;

  if (!reviewConfig) {
    return true;
  }

  if (
    reviewConfig.userIds.includes(value?.userID as string) ||
    reviewConfig.roles.includes(
      value?.role as unknown as WorkflowLimitedUserTypes,
    )
  ) {
    return true;
  }

  return false;
}

export function personaName(persona: string) {
  const map = flagPayload("persona-nicknames");
  const wordCase =
    persona.slice(0, 1).toUpperCase() + persona.slice(1).toLowerCase();
  return (
    map?.[persona] ??
    map?.[wordCase] ??
    (persona === "no_rule" ? "" : persona.replace(/_/g, " "))
  );
}

export function canSetTicketStatus(ticket: GetTicketResponse) {
  const { value } = store.getState().auth;

  // always true for role mg and admin
  if (
    value?.role === EnterpriseProfileType.MEANINGFUL_GIGS ||
    value?.role === EnterpriseProfileType.ADMIN ||
    value?.role === EnterpriseProfileType.ADMIN_WITH_AI
  ) {
    return true;
  }

  const config = ticket.workflow?.steps?.find(
    (step) => step.type === TicketWorkflowStep.DELIVERY,
  )?.config as DeliveryConfig;

  if (!config) {
    return true;
  }

  if (
    config.canSetTicketStatus.userIds.includes(value?.userID as string) ||
    config.canSetTicketStatus.roles.includes(
      value?.role as unknown as WorkflowLimitedUserTypes,
    )
  ) {
    return true;
  }

  return false;
}

export function canMarketingAccess(ticket: GetTicketResponse) {
  const { value } = store.getState().auth;

  const config = ticket.workflow?.steps?.find(
    (step) => step.type === TicketWorkflowStep.DESIGN_REVIEW,
  )?.config as DesignReviewConfig;

  if (!config) {
    return true;
  }

  if (
    config.canMarketingAccess ||
    value?.role !== EnterpriseProfileType.CATALYST_MARKETING
  ) {
    return true;
  }

  return false;
}

export function canApproveTicket(ticket: Ticket) {
  const { value } = store.getState().auth;

  if (value == null) {
    return false;
  }

  return TicketsAuth.canApproveTicket(
    ticket,
    value?.userID as string,
    value?.role as unknown as WorkflowUserTypes,
  );
}

export function canBeAssignedDesignReviewStep(ticket: Ticket) {
  const { value } = store.getState().auth;

  // current creatives should be able to submit revisions, independent of any config
  if (
    ticket.creatives?.find(
      (creative) => (creative as User)._id === value?.userID,
    )
  ) {
    return true;
  }

  // always true for role mg and admin
  if (
    value?.role === EnterpriseProfileType.MEANINGFUL_GIGS ||
    value?.role === EnterpriseProfileType.ADMIN ||
    value?.role === EnterpriseProfileType.ADMIN_WITH_AI
  ) {
    return true;
  }

  const config = ticket.workflow?.steps?.find(
    (step) => step.type === TicketWorkflowStep.DESIGN_REVIEW,
  )?.config as DesignReviewConfig;

  if (!config) {
    return true;
  }

  return (
    config.requiresApproval?.canBeAssigned?.userIds?.includes(
      value?.userID as string,
    ) ||
    config.requiresApproval?.canBeAssigned?.roles?.includes(
      value?.role as unknown as WorkflowUserTypes,
    )
  );
}

export function isDanoneBetaTester() {
  return userCanAccess("danone-beta-testers");
}

export function isAskPunttEnabledForEnterprise() {
  const data = queryClient.getQueryData<GetKnowledgeSettingsResponse>([
    "brand-guidelines",
  ]);
  return data?.askPunttEnabled === true;
}

/**
 * Documents primary sidebar nav — off when the viewer has Ask Puntt access
 * (MG role, Danone beta, or enterprise toggle). Those users reach Documents
 * via the Settings gear instead, including MG staff on enterprises where the
 * toggle is still off.
 */
export function showDocumentsInSidebar() {
  return !canAccessAskPuntt();
}

/** Documents in the Settings gear dropdown — on when Ask Puntt is accessible. */
export function showDocumentsInSettings() {
  return canAccessAskPuntt();
}

export function canAccessAskPuntt() {
  return (
    isMeaningfulGigsRole() ||
    isDanoneBetaTester() ||
    isAskPunttEnabledForEnterprise()
  );
}

/**
 * The admin/MG umbrella — exactly the roles that carry
 * `UserPermission.ENTERPRISE_ADMIN` in `EnterpriseProfileTypePermissions`
 * (`@mg/schemas/src/commons` → user.ts). See `canUseAskPunttConnectors`.
 */
const ASK_PUNTT_CONNECTOR_ROLES: EnterpriseProfileType[] = [
  EnterpriseProfileType.MEANINGFUL_GIGS,
  EnterpriseProfileType.ADMIN,
  EnterpriseProfileType.ADMIN_WITH_AI,
];

/**
 * AskPuntt connectors (Wrike / Asana / Hubspot / Frame.io / etc.) are a
 * power-user feature: they cost vendor API calls per search, surface
 * enterprise-wide content the operator may not have seen at the ticket
 * level, and the "Manage" link inside the popover routes straight into
 * integration settings which OPS users don't have admin permission for.
 *
 * The gate is the admin/MG umbrella: exactly the roles that carry
 * `UserPermission.ENTERPRISE_ADMIN` in `EnterpriseProfileTypePermissions`
 * (`@mg/schemas/src/commons` → user.ts) — MEANINGFUL_GIGS, ADMIN and
 * ADMIN_WITH_AI. Every other role reaches AskPuntt through the enterprise
 * toggle without that permission, so `/integration-settings` bounces them
 * (`canManageUsers()` guards it) and the popover's "Manage" entry is a dead
 * end.
 *
 * NOT A SECURITY BOUNDARY — this gates the UI surface and nothing else. No
 * server-side check mirrors it: `createChat`
 * (`services/christo/app/handlers/catalyst/askPuntt.ts`) filters the
 * client-supplied `enabledConnectors` against enterprise availability only
 * (`getEnterpriseAvailableAskPunttConnectorIds` → `policyEnabled &&
 * connected`), with no role check, and the askPunttProject /
 * askPunttProjectFeed paths resolve the same way; rembrandt then registers a
 * connector tool per surviving id. A denied role that POSTs the API directly
 * still gets connector search. So read the three reasons above as why the
 * section is admin-only in the product, NOT as a claim that the content
 * behind it is protected from a role that bypasses the client. Making it an
 * access boundary means the same ENTERPRISE_ADMIN check in christo (or on
 * rembrandt's tool registration) — a server change this file cannot make.
 *
 * SCOPE — why the whole connectors section, not just the "Manage" entry.
 * The dead end is only one of the three reasons above; the other two (a vendor
 * API call per search, and enterprise-wide content the viewer never saw at the
 * ticket level) are properties of connector SEARCH itself, which is what the
 * section does. Gating only the Manage link would leave both standing.
 *
 * Nor does the allowlist deny anything the original OPS decision (#3551) left
 * open. Read `EnterpriseProfileTypePermissions` and every newly-denied role
 * holds a strict subset of the OPS grant: CATALYST_MARKETING and CATALYST_AI
 * drop MANAGE_ASSETS and MANAGE_TEMPLATES, CATALYST_CREATIVE keeps
 * READ_TICKETS alone, and CATALYST_REQUESTER plus the four CONNECT_* roles
 * have no entry at all. Not one of them outranks the role product already
 * confirmed must not see connectors, so this applies that decision to the
 * rest of the table rather than extending it to a new tier.
 *
 * If client-side staff should later get connector search WITHOUT integration
 * admin, split the surface — search on its own predicate, "Manage" on
 * `canManageUsers()` — rather than re-widening this gate, which is the drift
 * described next.
 *
 * This is an ALLOWLIST on purpose. It used to be a denylist of OPS, then
 * OPS+EXPERT (#5709), then OPS+EXPERT+COORDINATOR (#5968) — each new role
 * silently defaulted to "sees connectors" until someone noticed. The denylist
 * also inverted the permission table it was meant to mirror: MARKETING,
 * CREATIVE, REQUESTER and the CONNECT_* roles all sit at or BELOW the OPS tier
 * yet saw the connectors UI that OPS is explicitly denied.
 *
 * Pin the gate here so both `home.tsx` and `chat.tsx` derive
 * `availableConnectors = []` consistently — without this the
 * connectors-trigger leaks into the non-admin attach popover whenever the
 * enterprise has any enabled connectors (Vitalora + every demo enterprise
 * does), which trips
 * `qa/rickrubin/tests/specs/cca/ask.puntt.connectors.spec.ts:203`
 * ("Not visible for OPS" — the browser-layer pin for the originally reported
 * OPS leak, #3551; the co-located `__tests__/auth.test.ts` is what pins the
 * predicate for every other role).
 */
export function canUseAskPunttConnectors() {
  const { value } = store.getState().auth;
  // Guard against the unauthenticated state — `canAccessAskPuntt()`
  // returns `true` purely from the enterprise toggle (since it doesn't
  // consult the user object), so without this check a logged-out call
  // path could derive `availableConnectors` from the cached enterprise
  // guidelines and leak the connectors UI. A null-role share-link guest
  // carries a non-null token, so the role allowlist below is what keeps
  // the connectors UI away from them.
  if (!value) return false;
  if (!canAccessAskPuntt()) return false;
  if (!value.role) return false;
  return ASK_PUNTT_CONNECTOR_ROLES.includes(value.role);
}

export function canAccessPuntt() {
  return userCanAccess("puntt-access");
}

export function canDismissComments() {
  return userCanAccess("dismiss-comments");
}

export function canResolveComments() {
  return userCanAccess("resolve-comments");
}

export function canDeleteComments() {
  return userCanAccess("delete-comments");
}

export function canThumbsDownAiComments() {
  return userCanAccess("ai-thumbs-down");
}

export function canCreateRevisions() {
  return userCanAccess("create-revisions");
}

export function canShowDismissedComments() {
  return userCanAccess("show-dismissed-comments");
}

export function canEnterpriseAdmin() {
  const { value } = store.getState().auth;
  return Boolean(value?.permissions?.includes(UserPermission.ENTERPRISE_ADMIN));
}

export function canManageBrandGuidelines() {
  // Demo tenant short-circuit: the posthog `manage-brand-guidelines`
  // flag isn't pinned to the demo user pool, so without this the route
  // bounces back to /tickets. Real tenants (including MG staff sitting
  // on a real customer) still go through the flag.
  if (userExists() && isDemoEnterprise()) return true;
  return userCanAccess("manage-brand-guidelines");
}

export function canManageUsers() {
  if (userExists() && isDemoEnterprise()) {
    const { value: user } = store.getState().auth;
    if (
      user?.role === EnterpriseProfileType.ADMIN ||
      user?.role === EnterpriseProfileType.ADMIN_WITH_AI ||
      user?.role === EnterpriseProfileType.MEANINGFUL_GIGS
    ) {
      return true;
    }
  }
  return userCanAccess("manage-users");
}

// Anyone with a "normal-or-above" enterprise role may invite people from
// the sidebar. Restricted (CATALYST_REQUESTER) share-link guests and
// logged-out / null-role users are excluded. The christo invite handler
// enforces the same allowlist server-side.
export function canInvitePeople() {
  if (!userExists()) return false;
  const { value: user } = store.getState().auth;
  return (
    user?.role === EnterpriseProfileType.OPS ||
    user?.role === EnterpriseProfileType.ADMIN ||
    user?.role === EnterpriseProfileType.ADMIN_WITH_AI ||
    user?.role === EnterpriseProfileType.MEANINGFUL_GIGS
  );
}

/**
 * Archive / resend-invite / remove-from-enterprise on Manage Users.
 *
 * Backend `authorize(CONNECT_TEAM)` concatenates role-derived permissions, so
 * ADMIN / ADMIN_WITH_AI / MEANINGFUL_GIGS can call those endpoints even when
 * the JWT `permissions` array is empty (invited-then-promoted client-type
 * MG owners). Gate on role as well as CONNECT_TEAM so the Actions column
 * matches who the API will actually allow.
 */
export function canArchiveUsers() {
  if (!userExists()) return false;
  const { value: user } = store.getState().auth;
  if (user?.permissions?.includes(UserPermission.CONNECT_TEAM)) {
    return true;
  }
  return (
    user?.role === EnterpriseProfileType.ADMIN ||
    user?.role === EnterpriseProfileType.ADMIN_WITH_AI ||
    user?.role === EnterpriseProfileType.MEANINGFUL_GIGS
  );
}

export function canDoRestrictedCommentEdit() {
  return userCanAccess("restricted-comment-edit");
}

export function canAccessKnowledgeDatabase() {
  // Demo tenant short-circuit: the posthog `knowledge-database` variant
  // isn't pinned to the demo user pool, so without this the route
  // bounces to /tickets. Real tenants still go through the flag.
  if (userExists() && isDemoEnterprise()) return true;
  const v = flagVariant("knowledge-database");
  return (
    v === "view" ||
    v === "manage" ||
    v === "mutate-records" ||
    v === "mutate-models"
  );
}

export function canManageKnowledgeDatabase() {
  if (userExists() && isDemoEnterprise()) return true;
  const v = flagVariant("knowledge-database");
  // Legacy mutate-records/mutate-models rollouts map to manage (no flag-day).
  return v === "manage" || v === "mutate-records" || v === "mutate-models";
}

export function canChooseCommentPersonas() {
  return userCanAccess("comment-personas");
}

export function canAccessPunttDashboard() {
  return userCanAccess("puntt-dashboard");
}

export function canUploadTickets() {
  const { value: user } = store.getState().auth;
  return (
    user?.role === EnterpriseProfileType.MEANINGFUL_GIGS ||
    user?.role === EnterpriseProfileType.CATALYST_AI ||
    user?.role === EnterpriseProfileType.ADMIN_WITH_AI
  );
}

export function canAddEnterprise() {
  const { value: user } = store.getState().auth;

  // See services/christo/app/handlers/catalyst/enterprises.ts@createEnterprise
  return user?.role === EnterpriseProfileType.MEANINGFUL_GIGS;
}

export function isMeaningfulGigsRole() {
  const { value: user } = store.getState().auth;
  return user?.role === EnterpriseProfileType.MEANINGFUL_GIGS;
}

/**
 * True when the active enterprise is the resettable demo tenant
 * (Enterprise.isDemo === true). Used to widen UI surfaces normally gated to
 * MG staff so all six demo personas (Sarah, Priya, James, Tom, Mia, Alex)
 * see the same chrome on the demo tenant — without granting any of those
 * widenings to real customers.
 *
 * Reads the enterprise list from the same react-query cache the rest of the
 * app uses (`useEnterprises()`), so it is synchronous and consistent with
 * what the user sees in the EnterpriseSwitcher. Returns false until the
 * cache is hydrated, which is the safe default.
 */
export function isDemoEnterprise() {
  const { value: user } = store.getState().auth;
  if (!user?.enterpriseId) return false;
  const enterprises = queryClient.getQueryData<Enterprise[]>(["enterprises"]);
  if (!enterprises) return false;
  const current = enterprises.find((e) => e._id === user.enterpriseId);
  return current?.isDemo === true;
}

/**
 * Canonical slug of the seeded Vitalora sales-demo enterprise
 * (`packages/commons-node/src/libs/demoConstants.ts` → DEMO_ENTERPRISE_SLUG).
 * Kept as a FE constant so Carrie does not import commons-node.
 */
export const VITALORA_DEMO_ENTERPRISE_SLUG = "vitalora";

/**
 * True only on the seeded Vitalora demo tenant (isDemo + slug `vitalora`).
 * Use for Vitalora-specific Demo Settings chrome (brand config, reset,
 * killer flow, etc.). Other `isDemo` enterprises used for sales sandboxes
 * must NOT see that surface.
 */
export function isVitaloraDemoEnterprise() {
  const { value: user } = store.getState().auth;
  if (!user?.enterpriseId) return false;
  const enterprises = queryClient.getQueryData<Enterprise[]>(["enterprises"]);
  if (!enterprises) return false;
  const current = enterprises.find((e) => e._id === user.enterpriseId);
  return (
    current?.isDemo === true && current.slug === VITALORA_DEMO_ENTERPRISE_SLUG
  );
}

/**
 * True for MG staff OR any user logged into a demo enterprise (`isDemo`).
 * Use for non-destructive UI gates that apply to all demo sandboxes (e.g.
 * ROI sidebar). Vitalora-only Demo Settings must use
 * `isVitaloraDemoEnterprise` instead. Destructive backend actions
 * (`resetDemoEnvironment`) stay strictly MG-only — the role widening
 * here is presentation-layer.
 */
export function isMeaningfulGigsOrDemo() {
  return isMeaningfulGigsRole() || isDemoEnterprise();
}

export function canShowDeletedComments() {
  const { value: user } = store.getState().auth;
  return (
    user?.role === EnterpriseProfileType.MEANINGFUL_GIGS ||
    user?.role === EnterpriseProfileType.CATALYST_AI
  );
}

function userCanAccess(flag: Parameters<typeof isFlagOn>[0]) {
  return userExists() && isFlagOn(flag);
}

function userExists() {
  const user = store.getState().auth.value;

  return user != null;
}
